import React, { useEffect, useRef, useState } from "react";
import {
  Signal,
  Tag,
  FolderOpen,
  CalendarClock,
  Briefcase,
} from "lucide-react";
import {
  List,
  Tag as TagType,
  BuiltInFilterState,
  ScheduledFilterValue,
  WorkHoursFilterValue,
} from "../../types";
import BuiltInFilterButton from "./BuiltInFilterButton";
import FilterPopover from "./FilterPopover";
import AnimatePresence from "../ui/AnimatePresence";
import SharedListMark from "../ui/SharedListMark";
import { useOrderedLists } from "../../hooks/queries/useOrderedLists";

const PRIORITY_OPTIONS = [
  { value: 1, label: "High", emoji: "🔴" },
  { value: 2, label: "Medium", emoji: "🟠" },
  { value: 3, label: "Low", emoji: "🟡" },
];

// Tri-state: Both = no constraint, Only = restrict to work-hours tasks,
// Without = hide them.
const WORK_HOURS_OPTIONS = [
  { value: null, label: "Both", emoji: "📋" },
  { value: "only" as WorkHoursFilterValue, label: "Only", emoji: "💼" },
  { value: "exclude" as WorkHoursFilterValue, label: "Without", emoji: "🌙" },
] as const;

const SCHEDULED_OPTIONS = [
  { value: "overdue" as ScheduledFilterValue, label: "Overdue", emoji: "⏰" },
  { value: "unscheduled" as ScheduledFilterValue, label: "Unscheduled", emoji: "📭" },
  { value: "scheduled" as ScheduledFilterValue, label: "Scheduled", emoji: "🗓️" },
] as const;

function oneScheduledLabel(
  values: ScheduledFilterValue[],
): string | undefined {
  if (values.length !== 1) return undefined;
  return SCHEDULED_OPTIONS.find((option) => option.value === values[0])?.label;
}

type OpenPopover =
  | "scheduled"
  | "priority"
  | "tags"
  | "lists"
  | "work_hours"
  | null;

interface BuiltInFiltersProps {
  builtInFilterState: BuiltInFilterState;
  onBuiltInFilterChange: (state: BuiltInFilterState) => void;
  lists: List[];
  tags: TagType[];
  /**
   * When provided, the lists popover hides any list whose id is not in this
   * set (unless it's already selected). Pass `undefined` to show all lists —
   * appropriate for the calendar side panel where users want every list.
   */
  nonEmptyListIds?: Set<string>;
  /**
   * Hides the Schedule chip entirely. Used by the calendar side panel,
   * where "Schedule" status is the panel's whole purpose — surfacing it
   * as a sub-filter is redundant and confusing.
   */
  hideScheduledFilter?: boolean;
  /** When the global shortcut-hints overlay is active, popovers are force-closed. */
  shortcutHintsActive?: boolean;
  className?: string;
}

const BuiltInFilters: React.FC<BuiltInFiltersProps> = ({
  builtInFilterState,
  onBuiltInFilterChange,
  lists,
  tags,
  nonEmptyListIds,
  hideScheduledFilter,
  shortcutHintsActive,
  className = "flex items-center gap-2 flex-wrap",
}) => {
  const [openPopover, setOpenPopover] = useState<OpenPopover>(null);
  const { lists: visibleLists, sharedListIds } = useOrderedLists(lists);

  const scheduledBtnRef = useRef<HTMLButtonElement>(null);
  const priorityBtnRef = useRef<HTMLButtonElement>(null);
  const tagsBtnRef = useRef<HTMLButtonElement>(null);
  const listsBtnRef = useRef<HTMLButtonElement>(null);
  const workHoursBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (shortcutHintsActive) setOpenPopover(null);
  }, [shortcutHintsActive]);

  const togglePopover = (popover: OpenPopover) => {
    setOpenPopover((prev) => (prev === popover ? null : popover));
  };

  const toggleScheduled = (value: ScheduledFilterValue) => {
    const current = builtInFilterState.scheduledFilter;
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    onBuiltInFilterChange({ ...builtInFilterState, scheduledFilter: next });
  };

  const setWorkHoursFilter = (value: WorkHoursFilterValue | null) => {
    onBuiltInFilterChange({
      ...builtInFilterState,
      workHoursFilter:
        builtInFilterState.workHoursFilter === value ? null : value,
    });
  };

  const togglePriority = (value: number) => {
    const current = builtInFilterState.priorities;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onBuiltInFilterChange({ ...builtInFilterState, priorities: next });
  };

  const toggleList = (listId: string | null) => {
    const current = builtInFilterState.listIds;
    const next = current.includes(listId)
      ? current.filter((v) => v !== listId)
      : [...current, listId];
    onBuiltInFilterChange({ ...builtInFilterState, listIds: next });
  };

  const toggleTag = (tagId: string) => {
    const current = builtInFilterState.tagIds;
    const next = current.includes(tagId)
      ? current.filter((v) => v !== tagId)
      : [...current, tagId];
    onBuiltInFilterChange({ ...builtInFilterState, tagIds: next });
  };

  const showTagsButton = tags.length > 0;
  const showListsButton = lists.length > 0;

  return (
    <>
      <div className={className}>
        {!hideScheduledFilter && (
          <BuiltInFilterButton
            label="Scheduled"
            icon={CalendarClock}
            activeCount={builtInFilterState.scheduledFilter.length}
            isPopoverOpen={openPopover === "scheduled"}
            onTogglePopover={() => togglePopover("scheduled")}
            onClear={() =>
              onBuiltInFilterChange({
                ...builtInFilterState,
                scheduledFilter: [],
              })
            }
            buttonRef={scheduledBtnRef}
            shortcutKey="S"
            activeLabel={oneScheduledLabel(builtInFilterState.scheduledFilter)}
          />
        )}
        <BuiltInFilterButton
          label="Priority"
          icon={Signal}
          activeCount={builtInFilterState.priorities.length}
          isPopoverOpen={openPopover === "priority"}
          onTogglePopover={() => togglePopover("priority")}
          onClear={() =>
            onBuiltInFilterChange({ ...builtInFilterState, priorities: [] })
          }
          buttonRef={priorityBtnRef}
          shortcutKey="P"
          activeLabel={
            builtInFilterState.priorities.length === 1
              ? (() => {
                  const p = PRIORITY_OPTIONS.find(
                    (o) => o.value === builtInFilterState.priorities[0],
                  );
                  return p ? `${p.emoji} ${p.label}` : undefined;
                })()
              : undefined
          }
        />
        {showTagsButton && (
          <BuiltInFilterButton
            label="Tags"
            icon={Tag}
            activeCount={builtInFilterState.tagIds.length}
            isPopoverOpen={openPopover === "tags"}
            onTogglePopover={() => togglePopover("tags")}
            onClear={() =>
              onBuiltInFilterChange({ ...builtInFilterState, tagIds: [] })
            }
            buttonRef={tagsBtnRef}
            shortcutKey="T"
            activeLabel={
              builtInFilterState.tagIds.length === 1
                ? tags.find((t) => t.id === builtInFilterState.tagIds[0])?.name
                : undefined
            }
          />
        )}
        {showListsButton && (
          <BuiltInFilterButton
            label="Lists"
            icon={FolderOpen}
            activeCount={builtInFilterState.listIds.length}
            isPopoverOpen={openPopover === "lists"}
            onTogglePopover={() => togglePopover("lists")}
            onClear={() =>
              onBuiltInFilterChange({ ...builtInFilterState, listIds: [] })
            }
            buttonRef={listsBtnRef}
            shortcutKey="L"
            activeLabel={
              builtInFilterState.listIds.length === 1
                ? builtInFilterState.listIds[0] === null
                  ? "📭 No list"
                  : (() => {
                      const list = lists.find(
                        (l) => l.id === builtInFilterState.listIds[0],
                      );
                      return list ? `${list.emoji} ${list.name}` : undefined;
                    })()
                : undefined
            }
          />
        )}
        <BuiltInFilterButton
          label="9–5"
          icon={Briefcase}
          activeCount={builtInFilterState.workHoursFilter ? 1 : 0}
          isPopoverOpen={openPopover === "work_hours"}
          onTogglePopover={() => togglePopover("work_hours")}
          onClear={() =>
            onBuiltInFilterChange({
              ...builtInFilterState,
              workHoursFilter: null,
            })
          }
          buttonRef={workHoursBtnRef}
          shortcutKey="9"
          activeLabel={
            builtInFilterState.workHoursFilter === "only"
              ? "💼 Only"
              : builtInFilterState.workHoursFilter === "exclude"
                ? "🌙 Without"
                : undefined
          }
        />
      </div>

      <AnimatePresence popover>
        {openPopover === "work_hours" && (
          <FilterPopover
            anchorRef={workHoursBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Filter by working hours"
          >
            <div className="flex flex-col gap-1">
              {WORK_HOURS_OPTIONS.map((opt) => {
                const isActive =
                  (builtInFilterState.workHoursFilter ?? null) === opt.value;
                return (
                  <button
                    key={opt.label}
                    onClick={() => setWorkHoursFilter(opt.value)}
                    aria-pressed={isActive}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                      isActive
                        ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                        : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                    }`}
                  >
                    <span>{opt.emoji}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>

      <AnimatePresence popover>
        {openPopover === "scheduled" && (
          <FilterPopover
            anchorRef={scheduledBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Filter by schedule"
          >
            <div className="flex flex-col gap-1">
              {SCHEDULED_OPTIONS.map((opt) => {
                const isActive = builtInFilterState.scheduledFilter.includes(opt.value);
                return (
                  <button
                    key={opt.label}
                    onClick={() => toggleScheduled(opt.value)}
                    aria-pressed={isActive}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                      isActive
                        ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                        : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                    }`}
                  >
                    <span>{opt.emoji}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>

      <AnimatePresence popover>
        {openPopover === "priority" && (
          <FilterPopover
            anchorRef={priorityBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Filter by priority"
          >
            <div className="flex flex-col gap-1">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => togglePriority(opt.value)}
                  aria-pressed={builtInFilterState.priorities.includes(opt.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                    builtInFilterState.priorities.includes(opt.value)
                      ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                      : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                  }`}
                >
                  <span>{opt.emoji}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>

      <AnimatePresence popover>
        {openPopover === "tags" && (
          <FilterPopover
            anchorRef={tagsBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Filter by tags"
          >
            <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  aria-pressed={builtInFilterState.tagIds.includes(tag.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                    builtInFilterState.tagIds.includes(tag.id)
                      ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                      : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span>{tag.name}</span>
                </button>
              ))}
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>

      <AnimatePresence popover>
        {openPopover === "lists" && (
          <FilterPopover
            anchorRef={listsBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Filter by lists"
          >
            <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
              <button
                key="__no-list__"
                onClick={() => toggleList(null)}
                aria-pressed={builtInFilterState.listIds.includes(null)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                  builtInFilterState.listIds.includes(null)
                    ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                    : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                }`}
              >
                <span>📭</span>
                <span>No list</span>
              </button>
              {/* When nonEmptyListIds is provided (board), hide empty lists —
                  but keep already-selected ones visible so the user can
                  deselect them after their tasks have moved. When undefined
                  (calendar side panel), show every list. */}
              {visibleLists
                .filter(
                  (l) =>
                    !nonEmptyListIds ||
                    nonEmptyListIds.has(l.id) ||
                    builtInFilterState.listIds.includes(l.id),
                )
                .map((l) => (
                  <button
                    key={l.id}
                    onClick={() => toggleList(l.id)}
                    aria-pressed={builtInFilterState.listIds.includes(l.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                      builtInFilterState.listIds.includes(l.id)
                        ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                        : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                    }`}
                  >
                    <span>{l.emoji}</span>
                    <span>{l.name}</span>
                    {sharedListIds?.has(l.id) && <SharedListMark size={11} />}
                  </button>
                ))}
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>
    </>
  );
};

export default BuiltInFilters;
